import React, { Suspense } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, MemoryRouter } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import ImprovedErrorBoundary from '@/components/ImprovedErrorBoundary';
import SentryErrorBoundary from '@/components/SentryErrorBoundary';
import { AuthProvider } from '@/hooks/AuthProvider';
import { SupabaseConnectionProvider } from '@/hooks/SupabaseConnectionProvider';
import { RouteProviders } from '@/providers/RouteProviders';
import { DevelopmentConnectionStatus } from '@/components/ConnectionStatus';
import { queryClient, shouldShowDevtools } from '@/lib/query-client';
import { UnderConstructionModal } from '@/components/UnderConstructionModal';
import { isUnderConstructionDomain } from '@/lib/domain';

/**
 * 로딩 폴백 컴포넌트
 */
const LoadingFallback: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

/**
 * 에러 폴백 컴포넌트
 */
const ErrorFallback: React.FC<{ error: Error; resetError: () => void }> = ({ 
  error, 
  resetError 
}) => (
  <div className="flex flex-col items-center justify-center min-h-screen p-4">
    <div className="text-center space-y-4">
      <h2 className="text-2xl font-bold text-red-600">문제가 발생했습니다</h2>
      <p className="text-gray-600 max-w-md">
        {error.message || '알 수 없는 오류가 발생했습니다.'}
      </p>
      <button
        onClick={resetError}
        className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
      >
        다시 시도
      </button>
    </div>
  </div>
);

/**
 * React Query Provider
 * - QueryClient 설정 및 제공
 * - 개발 환경에서 DevTools 제공
 */
const QueryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  );
};

/**
 * 라우터 Provider
 * - React Router 설정
 * - Safari 호환성을 위한 안전한 처리
 */
const RouterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true, // React 18 호환성
        v7_relativeSplatPath: true, // 향후 버전 호환성
      }}
    >
      {children}
    </BrowserRouter>
  );
};

/**
 * UI Provider
 * - Tooltip Provider
 * - Toast 알림 시스템 (커스텀 Toaster)
 * - "준비 중" 모달 (app.wholsee.kr 도메인 전용)
 */
const UIProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <TooltipProvider delayDuration={300}>
      {children}
      <Toaster />
      {/* app.wholsee.kr 도메인에서만 "준비 중" 모달 표시 */}
      {isUnderConstructionDomain() && <UnderConstructionModal />}
    </TooltipProvider>
  );
};

/**
 * 연결 상태 Provider
 * - Supabase 연결 상태 관리
 * - 네트워크 연결 상태 모니터링
 */
const ConnectionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <SupabaseConnectionProvider>
      {children}
    </SupabaseConnectionProvider>
  );
};

/**
 * 인증 Provider
 * - 사용자 인증 상태 관리
 * - 세션 관리 및 자동 갱신
 */
const AuthenticationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <AuthProvider>
      {children}
    </AuthProvider>
  );
};

/**
 * Core Providers
 * - 핵심 기능들을 위한 Provider 조합
 * - 에러 경계 및 로딩 처리 포함
 * - 경로 기반 Context 분리 적용
 */
const CoreProviders: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <ImprovedErrorBoundary>
      <QueryProvider>
        <ConnectionProvider>
          <AuthenticationProvider>
            <RouteProviders>
              <Suspense fallback={<LoadingFallback />}>
                {children}
                {/* 개발 환경에서 연결 상태 모니터링 */}
                <DevelopmentConnectionStatus />
              </Suspense>
            </RouteProviders>
          </AuthenticationProvider>
        </ConnectionProvider>
      </QueryProvider>
    </ImprovedErrorBoundary>
  );
};

/**
 * 메인 앱 Provider
 * - 모든 Provider를 조합하여 앱 전체에 제공
 * - Safari 브라우저 호환성 고려
 * - 에러 처리 및 로딩 상태 관리
 */
export const AppProviders: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Safari에서 발생할 수 있는 모듈 로딩 오류 방지
  React.useEffect(() => {
    // 브라우저 호환성 체크
    const userAgent = navigator.userAgent.toLowerCase();
    const isSafari = userAgent.includes('safari') && !userAgent.includes('chrome');
    
    if (isSafari) {
      console.log('Safari 브라우저 감지됨 - 호환성 모드 활성화');
      
      // Safari 관련 성능 최적화
      if ('requestIdleCallback' in window === false) {
        (window as typeof window & { 
          requestIdleCallback?: (callback: IdleRequestCallback) => number; 
        }).requestIdleCallback = (callback: IdleRequestCallback) => {
          const start = Date.now();
          return setTimeout(() => {
            callback({
              didTimeout: false,
              timeRemaining() {
                return Math.max(0, 50 - (Date.now() - start));
              },
            });
          }, 1);
        };
      }
    }
  }, []);

  return (
    <RouterProvider>
      <UIProvider>
        <CoreProviders>
          {children}
        </CoreProviders>
      </UIProvider>
    </RouterProvider>
  );
};

/**
 * 개발 환경용 Provider 래퍼
 * - 개발 도구 및 디버깅 기능 제공
 */
export const DevAppProviders: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (process.env.NODE_ENV !== 'development') {
    return <AppProviders>{children}</AppProviders>;
  }

  return (
    <AppProviders>
      {children}
    </AppProviders>
  );
};

/**
 * 테스트 환경용 Provider 래퍼
 * - 테스트에 최적화된 설정
 */
export const TestAppProviders: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // 테스트 환경에서는 라우터를 MemoryRouter로 변경
  const TestRouterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    return <MemoryRouter>{children}</MemoryRouter>;
  };

  return (
    <TestRouterProvider>
      <UIProvider>
        <CoreProviders>
          {children}
        </CoreProviders>
      </UIProvider>
    </TestRouterProvider>
  );
};


// 기본 내보내기
export default AppProviders;